import { Types } from 'mongoose';

export enum TaxReliefElegibleType {
  Yes = 'YES',
  No = 'NO',
  Partial = 'PARTIAL',
}

export enum ConsiderationType {
  Cash = 'CASH',
  Share = 'SHARE',
}

export enum ShareConsiderationAllocationMethod {
  Upload = 'UPLOAD',
  Automatic = 'AUTOMATIC',
}

export enum BuildingPdfStatus {
  NOT_STARTED = 'notStarted',
  COMPLETE = 'complete',
  PROCESSING = 'processing',
  FAILURE = 'failure',
}

export enum ExitStatementStatus {
  Draft = 'DRAFT',
  Submitted = 'SUBMITTED',
}

export enum ConsiderationHistoryItemType {
  ShareConsiderationExit = 'shareConsiderationExit',
  CashExit = 'cashExit',
}

export interface SupportingFile {
  name: string;
  path: string;
}

export interface SupportingFileWithPreview extends SupportingFile {
  preview: string;
}

export interface ExitStatement {
  id: string;
  title: string;
  summary: string;
  letter: string;
  accountManagerId?: string;
  accountManager?: {
    name: string;
    profileImage: string;
    _id: string;
  };
  managerRole?: string;
  notes: string;
  supportingFiles?: Array<SupportingFileWithPreview>;
  exit: {
    date: string;
    taxReliefElegible: TaxReliefElegibleType;
  };
  amendment: {
    date: string;
  };
  firm: {
    whiteLabelConfig: {
      bgColor: string;
      primaryHoverColor: string;
      whiteBgLogo: {
        key: string;
      };
      showAccruedFeesInFeeBreakdown: boolean;
    };
    firmName: string;
    firmLogo: string;
    contactDetails: { email: string };
  };
  submitDate: string;
  companies: Array<{
    _id: string;
    tradingName: string;
    legalName?: string | null;
    shareClass?: string | null;
  }>;
  buildingPdfStatus: BuildingPdfStatus;
  createdAt: Date;
}

interface Investor {
  fullName: string;
  _id: Types.ObjectId;
}
export interface GetExitStatementResponse {
  exitStatement: ExitStatement;
  investors: Array<Investor>;
}

export type CreateExitStatementResponse = Record<string, never>;
export type UpdateExitStatementResponse = Record<string, never>;
export type DeleteExitStatementResponse = Record<string, never>;

export interface StageExitForCreationRequest {
  exitDate: string;
  companyIds: Array<string>;
  firmId: string;
  sharePrice: number;
  sharesToBeSold: number;
  shareAllocationUploadIds: Array<string>;
}

export interface StageExitForEditRequest {
  exitId: string;
  exitDate: string;
  firmId: string;
  sharePrice: number;
  isReceivedValueSharePriceUnchanged: boolean;
}

export interface StagedExitShareholding {
  shareholdingId: string;
  investorName: string;
  investor: unknown;
  tranche: { _id: string; fundName: string };
  noOfShares: number;
  sharesAllocatedForSale: number;
  sharePrice: number;
  accruedFeesCharged: number;
  previousGrossReceivedValue?: number;
  previousAccruedFeesCharged?: number;
  previousPerformanceFeesCharged?: number;
  externalInvestmentId?: string;
  additionalGrossReceivedValue?: number;
  totalSaleValue: number;
  performanceFee: number;
  suggestedSharesAllocatedForSale?: number;
  investorTotalAccruedFees?: number;
  shareAllocationRoundingDiff?: number;
  newSharesAllocated?: number;
}

export interface StageExitResponse {
  data: Array<StagedExitShareholding>;
}

export interface PreviousExitStatementPdf {
  exitStatementId: string;
  pdfUrl: string;
  exitDate: string;
  companyTradingName: string;
}

export interface GetExitStatementForInvestorResponse {
  exitStatement: ExitStatement & { previousExitStatementPdfs?: Array<PreviousExitStatementPdf> };
  financialMetrics?: Array<FinancialMetric>;
  investor: {
    _id: string;
    fullName: string;
    displayName: string;
    isDeceased: boolean;
  };
}

export interface AmendmentLog {
  exitId?: Types.ObjectId;
  exitAmendmentId?: Types.ObjectId | null;
  sourceAmendmentLogId?: Types.ObjectId | null;
  isInitial?: boolean | null;
  date?: Date | null;
  performanceFeesCharged?: number;
  accruedFeesCharged?: number;
  sharePrice?: number | null;
  taxableValueSharePrice?: number | null;
  noOfShares?: number | null;
  valueBeforeDeductions?: number;
  valueAfterDeductions?: number;
  taxableValue?: number | null;
  deleteForbidden?: boolean;
  exitStatementId?: Types.ObjectId;
}

export interface FinancialMetric {
  fundName?: string;
  investmentDate?: Date;
  noOfShares?: number | null;
  capitalAllocated?: number;
  valueAfterDeductions?: number;
  valueBeforeDeductions?: number | null;
  taxableValue?: number | null;
  multipleOnInvestment?: string;
  accruedFeesCharged?: number | null;
  performanceFeesCharged?: number | null;
  isAmendment: boolean;
  differential?: number;
  previousProceeds?: number;
  additionalProceeds?: number;
  netToReturn: number;
  netCostAfterIncomeTaxRelief: number;
  eligibleLoss: number;
  previousNetSaleProceeds?: number;
  totalNetSaleProceeds?: number;
  showEligibleLoss?: boolean;
  showIncomeTaxRelief?: boolean;
  previousExitLogWithLargerEligibleLoss?: boolean;
  amendmentDate?: Date | null;
  sharePrice?: number | null;
  amendmentOrder?: number;
  singleShareClassLabel?: string | null;
  // New optional fields to support enhanced exit statement tables
  totalFundSubscription?: number | null;
  netTaxableProceeds?: number | null;
  taxableGainLoss?: number | null;
  taxableDifferenceSinceLastStatement?: number | null;
  // Cash consideration specific fields
  isShareConsiderationExit?: boolean;
  cashReceiptDate?: Date | null;
  grossCashProceedsReceived?: number | null;
  cashProceedsInEscrow?: number | null;
  transactionFeesDue?: number | null;
  netCashProceedsReceived?: number | null;
  aggregateNetCashProceeds?: number | null;
  aggregateMultipleOnAllocation?: string | null;
  // Share consideration specific fields
  considerationCompanyName?: string | null;
  considerationShareClass?: string | null;
  considerationSharesReceived?: number | null;
  considerationShareValue?: number | null;
  newSharesSinceLastStatement?: number | null;
  valueDifferenceSinceLastStatement?: number | null;
  shareMovementId?: string;
  shareholdingId?: string;
  shareAllocationUploadId?: string;
  overallGainLoss?: number | null;
}
